import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DataTable,
  TableContainer,
  Table,
  TableBody,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Dropdown,
  DataTableSkeleton,
  ToggleSmall
} from 'carbon-components-react';
import { ChevronLeft20 } from '@carbon/icons-react';
import { fetchCLOListing } from 'redux/clo';
import {
  selectCLOListing,
  selectLoadingCLOListing,
  selectAddingCLOAsFavourite,
  selectRemovingCLOAsFavourite,
  selectHidingCLO,
  selectUnhidingCLO
} from 'redux/clo/selectors';
import { selectIsAuthenticated } from 'redux/session/selectors';
import Utils from 'utilities/helper.util';
import CLOListingRow from './cloListingRow';
import { CustomPagination } from 'components';
import { SCREEN_SIZE } from 'types';

type OwnProps = {
  handleBackButtonClick: Function;
  enquiryDetail: any;
  handleCLOViewDetailsClick: Function;
  searchText: string;
  setSearchText: Function;
  order: string;
  setOrder: Function;
  screenSize: any;
  withHidden: boolean;
  setWithHidden: Function;
  currentPage: number;
  setCurrentPage: Function;
  pageOffset: number;
  setPageOffset: Function;
  isStandloneCLOListing: boolean;
  goToMyEnquiryPage: Function;
  setQuerySeed: Function;
  querySeed: string;
};

type Props = OwnProps;

const initFilterValues = {
  top3: false
};

const CLOListing: React.FC<Props | any> = (props: Props) => {
  const {
    handleBackButtonClick,
    enquiryDetail,
    handleCLOViewDetailsClick,
    searchText,
    setSearchText,
    order,
    setOrder,
    screenSize,
    withHidden,
    setWithHidden,
    currentPage,
    setCurrentPage,
    pageOffset,
    setPageOffset,
    isStandloneCLOListing,
    goToMyEnquiryPage,
    setQuerySeed,
    querySeed
  } = props;
  const smallScreen = [SCREEN_SIZE.SM];
  const middleScreen = [SCREEN_SIZE.MD];
  const bigScreen = [SCREEN_SIZE.LG, SCREEN_SIZE.XL, SCREEN_SIZE.MAX];
  const dispatch = useDispatch();
  const cloList = useSelector(selectCLOListing);
  const loadingCLOListing = useSelector(selectLoadingCLOListing);
  const addingCLOAsFavourite = useSelector(selectAddingCLOAsFavourite);
  const removingCLOAsFavourite = useSelector(selectRemovingCLOAsFavourite);
  const hidingCLO = useSelector(selectHidingCLO);
  const unhidingCLO = useSelector(selectUnhidingCLO);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [pageSize, setPageSize] = useState<number>(10);
  const [filterBy, setFilterBy] = useState<any>(
    searchText
      ? `name:"%${searchText}%",top3:false,withHidden:${withHidden}`
      : `top3:false,withHidden:${withHidden}`
  );
  const [filteredCLOListing, setFilteredCLOListing] = useState<Array<any>>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [showNoResultWarning, setShowNoResultWarning] = useState<boolean>(
    false
  );
  const [orderBy, setOrderBy] = useState<string>(
    order === '1' ? '' : order === '2' ? 'name' : '-name'
  );
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [skeletonColumnCount, setSkeletonColumnCount] = useState<number>(5);
  const [isPerformingUpdate, setIsPerformingUpdate] = useState<boolean>(false);

  const sortListItems = [
    {
      label: 'Default',
      value: '1'
    },
    {
      label: 'Name (A-Z)',
      value: '2'
    },
    {
      label: 'Name (Z-A)',
      value: '3'
    }
  ];

  const headers: Array<any> = [
    { key: 'id', header: '' },
    { key: 'name', header: '' },
    { key: 'locatorAmount', header: '' },
    { key: 'address', header: '' },
    { key: 'star', header: '' },
    { key: 'hidden', header: '' }
  ];

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setIsFirstLoad(false);
    !querySeed && setQuerySeed(cloList.Info.seed);
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const resetPagination = () => {
    setCurrentPage(1);
    setPageOffset(0);
  };

  const constructFilterString = (name: string, showHidden: boolean) => {
    const filterValues = {
      ...initFilterValues,
      name: name,
      withHidden: showHidden
    };
    const filterString = Object.entries({
      ...filterValues
    })
      .reduce(filterStringReducer, '')
      .substring(1);
    setFilterBy(filterString);
  };

  const filterStringReducer = (accumulator: any, currentValue: any) => {
    const [key, value] = currentValue;
    switch (key) {
      case 'name':
        accumulator = value ? `${accumulator},name:"%${value}%"` : accumulator;
        break;
      case 'withHidden':
        accumulator = `${accumulator},withHidden:${value}`;
        break;
      case 'top3':
        accumulator = `${accumulator},top3:${value}`;
        break;
      default:
        break;
    }
    return accumulator;
  };

  const debouncedFunction = useCallback(
    Utils.debounce(async (args: string[]) => {
      handleChangeSearchBy(args[0], args[1], args[2], args[3]);
    }, 500),
    []
  );

  const handleChangeSearchBy = (
    searchText: string,
    showHidden: any,
    cloList: any,
    querySeed: string
  ) => {
    setFilteredCLOListing([]);
    resetPagination();
    setIsFirstLoad(false);
    !querySeed && setQuerySeed(cloList.Info.seed);
    constructFilterString(searchText, showHidden);
  };

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
    if (e.target.value.length >= 2)
      debouncedFunction(e.target.value, withHidden, cloList, querySeed);
    if (e.target.value.length === 0)
      debouncedFunction(e.target.value, withHidden, cloList, querySeed);
  };

  const handleDropdownChange = (value: string) => {
    let order = '';
    if (value === '2') {
      order = 'name';
    } else if (value === '3') {
      order = '-name';
    }

    setIsFirstLoad(false);
    !querySeed && setQuerySeed(cloList.Info.seed);
    setOrderBy(order);
    setOrder(value);
    if (order !== orderBy) {
      setFilteredCLOListing([]);
      resetPagination();
    }
  };

  const getOrderOptionItem = (value: string) => {
    if (value) {
      const matched = sortListItems.filter((o: any) => value.includes(o.value));
      return matched[0];
    }
    return [];
  };

  const handleToggleChange = (value: boolean) => {
    setFilteredCLOListing([]);
    resetPagination();
    setIsFirstLoad(false);
    setWithHidden(value);
    constructFilterString(searchText, value);
    !querySeed && setQuerySeed(cloList.Info.seed);
  };

  const handleBackClick = () => {
    if (isStandloneCLOListing) {
      goToMyEnquiryPage();
    } else {
      resetPagination();
      handleBackButtonClick();
    }
  };

  const handleCLORowClick = (cloId: number, from: string) => {
    !querySeed && setQuerySeed(cloList.Info.seed);
    handleCLOViewDetailsClick(cloId, from);
  };

  useEffect(() => {
    if (!loadingCLOListing && Object.keys(cloList).length !== 0) {
      const cloListInfo = cloList.Info;
      const cloListOrgs = cloList.LocatingOrganisations;

      const filteredList = cloListOrgs.map((org: any) => {
        const data = {
          id: org.id,
          name: org.name,
          locatorAmount: org.details.locatorAmount,
          address: org.Address,
          star: !!org.isFav,
          hidden: !!org.isHidden
        };
        return data;
      });
      setShowNoResultWarning(true);
      setFilteredCLOListing(filteredList);
      setTotalItems(cloListInfo.count);
    }
  }, [cloList, loadingCLOListing]);

  useEffect(() => {
    if (
      enquiryDetail &&
      Object.keys(enquiryDetail).length !== 0 &&
      !isPerformingUpdate &&
      isAuthenticated
    ) {
      const params = {
        filter: filterBy,
        limit: pageSize,
        offset: pageOffset,
        include: 'Address',
        extent: enquiryDetail?.shape,
        fields: 'id,name,details',
        orderBy: orderBy,
        seed: querySeed
      };
      dispatch(fetchCLOListing(params));
    }
  }, [
    enquiryDetail,
    dispatch,
    pageOffset,
    pageSize,
    currentPage,
    filterBy,
    orderBy,
    withHidden,
    isPerformingUpdate,
    querySeed,
    isAuthenticated
  ]);

  useEffect(() => {
    if (smallScreen.includes(screenSize)) {
      setSkeletonColumnCount(2);
    }
    if (middleScreen.includes(screenSize)) {
      setSkeletonColumnCount(3);
    }
    if (bigScreen.includes(screenSize)) {
      setSkeletonColumnCount(5);
    }
  }, [screenSize, bigScreen, middleScreen, smallScreen]);

  useEffect(() => {
    if (
      addingCLOAsFavourite ||
      removingCLOAsFavourite ||
      unhidingCLO ||
      hidingCLO
    ) {
      setIsPerformingUpdate(true);
    } else {
      setIsPerformingUpdate(false);
    }
  }, [addingCLOAsFavourite, removingCLOAsFavourite, unhidingCLO, hidingCLO]);

  return (
    <div className="clo-listing">
      <div className={'navigation-job-id'}>
        {!isStandloneCLOListing && (
          <ChevronLeft20 onClick={() => handleBackClick()} />
        )}
        <div className="job-id" onClick={() => handleBackClick()}>
          Job {enquiryDetail?.externalId}
        </div>
      </div>
      <div className="listing-container">
        <div className="header">
          <div className="title">
            All Certified Locating Organisations servicing job site
          </div>
          {enquiryDetail?.Address &&
            Object.keys(enquiryDetail?.Address).length !== 0 && (
              <div className="address">
                {Utils.getFullAddress(enquiryDetail.Address)}
              </div>
            )}
        </div>

        <div className="listing-table-container">
          {((!loadingCLOListing && !isPerformingUpdate) ||
            (loadingCLOListing && !isFirstLoad)) && (
            <DataTable headers={headers} rows={filteredCLOListing || []}>
              {({ getTableProps, getRowProps, rows }: any) => {
                return (
                  <TableContainer>
                    <div className="custom-table-toolbar">
                      <TableToolbar>
                        <TableToolbarContent>
                          <TableToolbarSearch
                            onChange={handleSearch}
                            expanded
                            placeHolderText="Filter"
                            value={searchText}
                          />
                        </TableToolbarContent>
                      </TableToolbar>
                      <Dropdown
                        id="sort"
                        titleText=""
                        label="Sort"
                        items={sortListItems}
                        itemToString={(item: any) => item.label}
                        initialSelectedItem={getOrderOptionItem(order)}
                        onChange={(item: any) =>
                          handleDropdownChange(item?.selectedItem?.value)
                        }
                      />
                    </div>
                    {!loadingCLOListing && filteredCLOListing.length >= 1 && (
                      <Table {...getTableProps()}>
                        <TableBody>
                          {rows.map((row: any, index: any) => (
                            <CLOListingRow
                              row={row}
                              getRowProps={getRowProps}
                              key={index}
                              handleCLOViewDetailsClick={handleCLORowClick}
                              screenSize={screenSize}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    {!loadingCLOListing &&
                      filteredCLOListing.length === 0 &&
                      showNoResultWarning && (
                        <div className="no-result">No results found</div>
                      )}
                    {loadingCLOListing && (
                      <div className="clo-loading-container">
                        <DataTableSkeleton
                          showHeader={false}
                          showToolbar={false}
                          compact={true}
                          rowCount={5}
                          columnCount={skeletonColumnCount}
                        />
                      </div>
                    )}
                  </TableContainer>
                );
              }}
            </DataTable>
          )}
          {(isPerformingUpdate || loadingCLOListing) && isFirstLoad && (
            <DataTableSkeleton
              showHeader={false}
              showToolbar={false}
              compact={true}
              rowCount={5}
              columnCount={skeletonColumnCount}
            />
          )}
          {filteredCLOListing.length >= 1 && (
            <>
              <CustomPagination
                onChange={(data: any) => handlePageChange(data)}
                totalItems={totalItems}
                page={currentPage}
                pageSize={pageSize}
              />
            </>
          )}
        </div>
      </div>
      <div className="show-all-toggle-container">
        <ToggleSmall
          defaultToggled={withHidden}
          aria-label="Show hidden"
          labelA="Show hidden"
          labelB="Show hidden"
          id="toggle-show-hidden"
          onToggle={(value: boolean) => handleToggleChange(value)}
        />
      </div>
      <div className="footer-text">
        <span>About this list.</span> The default list view is in a random order
        to ensure that all Certified Locating Organisations (CLOs) get the same
        opportunity to appear. The Certified Locator program is owned and
        operated by
        <a
          href="https://dbydlocator.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          CERTLOC Global
        </a>
        .
      </div>
    </div>
  );
};

export default CLOListing;
